import { Box, Checkbox, MenuItem, TextField, Typography } from '@mui/material';
import { Filter } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { RangeSlider } from '../RangeSlider';
import { CustomButton } from '../StyledComponents';
import { FilterDropdown } from './FilterDropdown';
import {
  BPM_MAX,
  BPM_MIN,
  BPM_STEP,
  defaultFilters,
  DURATION_MAX,
  DURATION_MIN,
  DURATION_STEP,
  formatDuration,
  GenreOption,
  isFilterActive,
  LEVEL_MAX,
  LEVEL_MIN,
  RepertoireFilters,
  RepertoireStatus,
  STATUS_OPTIONS,
} from './repertoireFilters';

const ACCENT = '#6F87F1';
const SLIDER_RAIL = '#2D3A67';

interface RepertoireFilterMenuProps {
  /** Genre choices, derived from the loaded repertoire list. */
  genreOptions: GenreOption[];
  /** Returns how many pieces match the given filters (drives the live count). */
  getResultCount: (filters: RepertoireFilters) => number;
  /** Called when the user commits a filter via "See Results". */
  onApply?: (filters: RepertoireFilters) => void;
  /** Called when the user clears all filters. */
  onClear?: () => void;
}

const selectMenuProps = {
  PaperProps: {
    sx: {
      mt: '6px',
      color: '#ECECED',
      backgroundColor: '#10192E',
      border: '1px solid #3E4C73',
      borderRadius: '10px',
      maxHeight: '260px',
      '& .MuiMenuItem-root.Mui-selected': { backgroundColor: '#243056' },
    },
  },
  sx: { zIndex: 1600 },
};

const selectSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    minHeight: '46px',
    color: '#ECECED',
    backgroundColor: 'transparent',
    borderRadius: '8px',
    fontFamily: 'Lato',
    fontSize: '15px',
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3A4256' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#5A668A' },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ACCENT },
  '& .MuiSvgIcon-root': { color: '#9AA3BD' },
};

const sectionTitleSx = {
  fontFamily: 'Lato',
  fontWeight: 700,
  fontSize: '16px',
  color: '#FFFFFF',
  textAlign: 'left' as const,
  mb: '14px',
};

const Divider = () => <Box sx={{ height: '1px', backgroundColor: '#1E2638', my: '20px' }} />;

/** Gray pill label that rides above a slider handle. */
const SliderLabel = ({ text }: { text: string }) => (
  <Box
    sx={{
      backgroundColor: '#3A3F4B',
      color: '#FFFFFF',
      fontFamily: 'Lato',
      fontSize: '12px',
      px: '8px',
      py: '2px',
      borderRadius: '5px',
      whiteSpace: 'nowrap',
    }}
  >
    {text}
  </Box>
);

export const RepertoireFilterMenu: React.FC<RepertoireFilterMenuProps> = ({
  genreOptions,
  getResultCount,
  onApply,
  onClear,
}) => {
  const buttonRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState<RepertoireFilters>(defaultFilters);
  const [appliedActive, setAppliedActive] = useState(false);

  const active = isFilterActive(filters);

  // Live result count, recomputed from the draft filters (client-side for now).
  const count = useMemo(() => getResultCount(filters), [getResultCount, filters]);

  const toggleStatus = useCallback((status: RepertoireStatus) => {
    setFilters((prev) => ({
      ...prev,
      statuses: prev.statuses.includes(status)
        ? prev.statuses.filter((s) => s !== status)
        : [...prev.statuses, status],
    }));
  }, []);

  // A genre option may stand for several underlying ids (same name, different
  // ids). Toggling adds/removes the whole set so it matches any tagged piece.
  const toggleGenre = useCallback((ids: number[]) => {
    setFilters((prev) => {
      const selected = ids.some((id) => prev.genreIds.includes(id));
      const next = new Set(prev.genreIds);
      if (selected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return { ...prev, genreIds: Array.from(next) };
    });
  }, []);

  // Interdependent level ranges: min can't exceed max, and vice versa.
  const minLevelOptions = useMemo(() => {
    const opts: number[] = [];
    for (let i = LEVEL_MIN; i <= filters.maxLevel; i++) opts.push(i);
    return opts;
  }, [filters.maxLevel]);

  const maxLevelOptions = useMemo(() => {
    const opts: number[] = [];
    for (let i = filters.minLevel; i <= LEVEL_MAX; i++) opts.push(i);
    return opts;
  }, [filters.minLevel]);

  const handleSeeResults = useCallback(() => {
    setAppliedActive(isFilterActive(filters));
    onApply?.(filters);
    setOpen(false);
  }, [filters, onApply]);

  const handleClear = useCallback(() => {
    setFilters(defaultFilters);
    setAppliedActive(false);
    onClear?.();
  }, [onClear]);

  const showClear = active || appliedActive;
  const buttonActive = open || appliedActive;

  return (
    <>
      <Box
        ref={buttonRef}
        role="button"
        aria-label="Filter repertoire"
        onClick={() => setOpen((o) => !o)}
        sx={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          backgroundColor: buttonActive ? ACCENT : '#0F1729',
          transition: 'background-color 0.2s ease-in-out',
          '&:hover': { backgroundColor: buttonActive ? ACCENT : '#1A2540' },
          position: 'relative',
        }}
      >
        <Filter size={20} color="#FFFFFF" fill={buttonActive ? '#FFFFFF' : 'none'} />
      </Box>

      <FilterDropdown open={open} anchorRef={buttonRef} onClose={() => setOpen(false)} width={460}>
        <Box sx={{ px: '20px', pt: '18px', pb: '20px' }}>
          <Typography
            sx={{
              fontFamily: 'Lato',
              fontWeight: 700,
              fontSize: '15px',
              letterSpacing: '2px',
              color: '#FFFFFF',
              textAlign: 'center',
              mb: '18px',
            }}
          >
            FILTERS
          </Typography>

          {/* Status — four equal-width pills on a single line */}
          <Typography sx={sectionTitleSx}>Status</Typography>
          <Box sx={{ display: 'flex', gap: '8px' }}>
            {STATUS_OPTIONS.map((opt) => {
              const selected = filters.statuses.includes(opt.id);
              return (
                <Box
                  key={opt.id}
                  role="button"
                  aria-pressed={selected}
                  onClick={() => toggleStatus(opt.id)}
                  sx={{
                    flex: '1 1 0',
                    minWidth: 0,
                    px: '4px',
                    py: '8px',
                    borderRadius: '20px',
                    fontFamily: 'Lato',
                    fontSize: '13px',
                    color: '#FFFFFF',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    cursor: 'pointer',
                    userSelect: 'none',
                    border: `1px solid ${selected ? ACCENT : '#3A4256'}`,
                    backgroundColor: selected ? 'rgba(111,135,241,0.18)' : 'transparent',
                    transition: 'all 0.15s ease-in-out',
                    '&:hover': { borderColor: ACCENT },
                  }}
                >
                  {opt.label}
                </Box>
              );
            })}
          </Box>

          <Divider />

          {/* Levels */}
          <Box sx={{ display: 'flex', gap: '16px' }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={sectionTitleSx}>Min. Level</Typography>
              <TextField
                select
                size="small"
                value={filters.minLevel}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, minLevel: Number(e.target.value) }))
                }
                SelectProps={{ MenuProps: selectMenuProps }}
                sx={selectSx}
              >
                {minLevelOptions.map((lvl) => (
                  <MenuItem key={lvl} value={lvl}>
                    {lvl}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={sectionTitleSx}>Max. Level</Typography>
              <TextField
                select
                size="small"
                value={filters.maxLevel}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, maxLevel: Number(e.target.value) }))
                }
                SelectProps={{ MenuProps: selectMenuProps }}
                sx={selectSx}
              >
                {maxLevelOptions.map((lvl) => (
                  <MenuItem key={lvl} value={lvl}>
                    {lvl}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>

          <Divider />

          {/* Genre */}
          <Typography sx={sectionTitleSx}>Genre</Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              rowGap: '8px',
              columnGap: '4px',
            }}
          >
            {genreOptions.map((genre) => {
              const checked = genre.ids.some((id) => filters.genreIds.includes(id));
              return (
                <Box
                  key={genre.id}
                  onClick={() => toggleGenre(genre.ids)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <Checkbox
                    checked={checked}
                    disableRipple
                    sx={{
                      p: '4px',
                      color: '#5A668A',
                      '&.Mui-checked': { color: ACCENT },
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: 'Lato',
                      fontSize: '15px',
                      color: '#FFFFFF',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {genre.name}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Divider />

          {/* Duration — disabled until the runtime calculation lands */}
          <Typography sx={sectionTitleSx}>
            Duration{' '}
            <Box
              component="span"
              sx={{ fontSize: '12px', fontWeight: 400, color: '#6C6C88', letterSpacing: 0 }}
            >
              (coming soon)
            </Box>
          </Typography>
          <Box sx={{ px: '10px', opacity: 0.4, pointerEvents: 'none' }}>
            <RangeSlider
              value={filters.durationRange}
              onChange={(range) => setFilters((prev) => ({ ...prev, durationRange: range }))}
              min={DURATION_MIN}
              max={DURATION_MAX}
              step={DURATION_STEP}
              markStep={180}
              disabled
              ariaLabelLeft="Minimum duration"
              ariaLabelRight="Maximum duration"
              ariaLabelGroup="Duration range"
              ariaValueTextLeft={(v) => formatDuration(v)}
              ariaValueTextRight={(v) => formatDuration(v)}
              thumbLeftColor={ACCENT}
              thumbRightColor={ACCENT}
              flipLabelPositionLeft
              flipLabelPositionRight
              labelLeft={<SliderLabel text={formatDuration(filters.durationRange[0])} />}
              labelRight={<SliderLabel text={formatDuration(filters.durationRange[1])} />}
              style={{
                track: { backgroundColor: SLIDER_RAIL },
                rail: { backgroundColor: SLIDER_RAIL },
                range: { background: ACCENT },
              }}
            />
          </Box>

          <Divider />

          {/* BPM */}
          <Typography sx={sectionTitleSx}>BPM</Typography>
          <Box sx={{ px: '10px' }}>
            <RangeSlider
              value={filters.bpmRange}
              onChange={(range) => setFilters((prev) => ({ ...prev, bpmRange: range }))}
              min={BPM_MIN}
              max={BPM_MAX}
              step={BPM_STEP}
              markStep={30}
              ariaLabelLeft="Minimum BPM"
              ariaLabelRight="Maximum BPM"
              ariaLabelGroup="BPM range"
              ariaValueTextLeft={(v) => `${v} bpm`}
              ariaValueTextRight={(v) => `${v} bpm`}
              thumbLeftColor={ACCENT}
              thumbRightColor={ACCENT}
              flipLabelPositionLeft
              flipLabelPositionRight
              labelLeft={<SliderLabel text={`${filters.bpmRange[0]} bpm`} />}
              labelRight={<SliderLabel text={`${filters.bpmRange[1]} bpm`} />}
              style={{
                track: { backgroundColor: SLIDER_RAIL },
                rail: { backgroundColor: SLIDER_RAIL },
                range: { background: ACCENT },
              }}
            />
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: '24px' }}>
            <CustomButton onClick={handleSeeResults} sx={{ minWidth: '220px' }}>
              {`SEE RESULTS (${count})`}
            </CustomButton>
            {showClear && (
              <Typography
                onClick={handleClear}
                sx={{
                  mt: '16px',
                  fontFamily: 'Lato',
                  fontWeight: 700,
                  fontSize: '14px',
                  letterSpacing: '1px',
                  color: '#FFFFFF',
                  cursor: 'pointer',
                  '&:hover': { color: '#ADB1B7' },
                }}
              >
                CLEAR RESULTS
              </Typography>
            )}
          </Box>
        </Box>
      </FilterDropdown>
    </>
  );
};

export default RepertoireFilterMenu;
